<script setup lang="ts">
export interface SocialLink {
  service: string;
  url: string;
}

export interface NavigationItem {
  id: string;
  title: string;
  url?: string | null;
  page?: {
    permalink?: string | null;
  };
}

export interface FooterProps {
  navigation: {
    items: NavigationItem[];
  };
  globals: {
    logo?: string | null;
    logo_dark_mode?: string | null;
    description?: string | null;
    social_links?: SocialLink[];
  };
}

const props = defineProps<FooterProps>();
const runtimeConfig = useRuntimeConfig();

// Using template ref to expose the footer to the layout for visual editing
const footerRef = useTemplateRef('footerRef');
defineExpose({ footerRef });

const lightLogoUrl = computed(() =>
  props.globals.logo ? `${runtimeConfig.public.directusUrl}/assets/${props.globals.logo}` : '/images/logo.svg',
);

const darkLogoUrl = computed(() =>
  props.globals.logo_dark_mode ? `${runtimeConfig.public.directusUrl}/assets/${props.globals.logo_dark_mode}` : '',
);
</script>

<template>
  <!-- <div class="relative w-full"> -->
  <div class="bg-tapis px-[--tapis] pb-[--tapis] relative w-full">
    <div class="absolute -top-4 w-full left-0 h-[110px] px-[--tapis]">
      <svg
        width="210.09555mm"
        height="54.009541mm"
        class="block w-full h-full pointer-events-none"
        viewBox="0 0 210.09555 54.009541"
        preserveAspectRatio="none"
        version="1.1"
        id="svg845"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:svg="http://www.w3.org/2000/svg"
      >
        <defs id="defs842">
          <clipPath clipPathUnits="userSpaceOnUse" id="clipPath17689">
            <path d="M 0,768 H 1366 V 0 H 0 Z" id="path17687" />
          </clipPath>
          <clipPath clipPathUnits="userSpaceOnUse" id="clipPath17689-0-5-7">
            <path d="M 0,768 H 1366 V 0 H 0 Z" id="path17687-2-2-3" />
          </clipPath>
        </defs>
        <g
          id="g17685-1-4-7"
          clip-path="url(#clipPath17689-0-5-7)"
          transform="matrix(0,0.06035401,0.27363541,0,0,-28.434019)"
        >
          <g id="g17883-8-0-3" transform="translate(737.3292,295.9507)">
            <path
              d="m 0,0 c 21.58,90.498 -204.45,118.56 -153.198,264.226 4.17557,21.48025 34.00738,53.55663 34.00738,53.55663 0,0 -32.23371,-28.38292 -48.67138,-53.55663 C -234.77051,118.86923 -84.239,90.498 -105.819,0 c -21.58,-90.498 -211.755,-67.104 -147.015,-295.951 h 14.665 C -302.91,-67.104 -21.58,-90.498 0,0"
              style="fill: #f1e6d5; fill-opacity: 1; fill-rule: nonzero; stroke: none"
              id="path17885-4-5-7"
            />
          </g>
          <g id="g17887-0-5-1" transform="translate(852.8751,295.9507)">
            <path
              d="m 0,0 c 21.58,90.498 -167.21413,118.69358 -225.75,264.226 -14.37583,35.74132 -12.84498,59.64724 -12.84498,59.64724 0,0 -13.40591,-34.85141 -30.14802,-59.64724 C -319.996,118.56 -93.966,90.498 -115.546,0 c -21.58,-90.498 -302.91,-67.104 -238.169,-295.951 h 42.993 C -375.462,-67.104 -21.58,-90.498 0,0"
              style="fill: #dfc4b5; fill-opacity: 1; fill-rule: nonzero; stroke: none"
              id="path17889-1-7-1"
            />
          </g>
          <g id="g17891-4-7-6" transform="translate(1046.144,295.9507)">
            <path
              d="m 0,0 c 21.58,90.498 -372.395,118.56 -321.142,264.226 9.39881,30.02174 -58.952,61.00594 -58.952,61.00594 l -8.70846,-0.65845 c 0,0 2.09413,-39.92284 -30.21654,-60.34749 C -470.271,118.56 -171.689,90.498 -193.269,0 c -21.58,-90.498 -375.462,-67.104 -310.722,-295.951 h 97.877 C -470.854,-67.104 -21.58,-90.498 0,0"
              style="fill: #ead8be; fill-opacity: 1; fill-rule: nonzero; stroke: none"
              id="path17893-3-8-2"
            />
          </g>
          <path
            d="m 729.19999,767.7937 -137.59123,-0.13848 c 0,0 -11.66008,-58.71726 36.16987,-204.82906 47.70321,-145.72465 439.94507,-176.37746 418.36507,-266.87546 -21.58,-90.498 -470.854,-67.104 -406.114,-295.951 h 62.201 663.769 v 767.794 z"
            style="fill: #f5ebdf; fill-opacity: 1; fill-rule: nonzero; stroke: none"
            id="path17897-3-8-1"
          />
        </g>
      </svg>
    </div>
    <footer v-if="globals" ref="footerRef" class="bg-beigeMedium dark:bg-[var(--background-variant-color)] py-16">
      <Container class="text-foreground">
        <div class="flex flex-col md:flex-row justify-between items-start gap-8 pt-8">
          <div class="flex-1">
            <NuxtLink to="/" class="inline-block transition-opacity hover:opacity-70">
              <img
                v-if="lightLogoUrl"
                :src="lightLogoUrl"
                alt="Logo"
                :class="['w-[120px] h-auto', darkLogoUrl ? 'dark:hidden' : '']"
              />
              <img
                v-if="darkLogoUrl"
                :src="darkLogoUrl"
                alt="Logo (Dark Mode)"
                class="w-[120px] h-auto hidden dark:block"
              />
            </NuxtLink>

            <p v-if="props.globals.description" class="text-description mt-2">
              {{ props.globals.description }}
            </p>

            <!-- Social Links -->
            <div v-if="props.globals.social_links?.length" class="mt-4 flex space-x-4">
              <a
                v-for="social in props.globals.social_links"
                :key="social.service"
                :href="social.url"
                target="_blank"
                rel="noopener noreferrer"
                class="size-8 rounded bg-transparent inline-flex items-center justify-center transition-colors hover:opacity-70"
              >
                <img
                  :src="`/icons/social/${social.service}.svg`"
                  :alt="`${social.service} icon`"
                  class="size-6 dark:invert"
                />
              </a>
            </div>
          </div>

          <div class="flex flex-col items-start md:items-end flex-1">
            <nav v-if="props.navigation.items?.length" class="w-full md:w-auto text-left">
              <ul class="space-y-4">
                <li v-for="item in props.navigation.items" :key="item.id">
                  <NuxtLink
                    v-if="item.page?.permalink"
                    :to="item.page.permalink"
                    class="text-nav font-medium hover:underline"
                  >
                    {{ item.title }}
                  </NuxtLink>
                  <a v-else :href="item.url || '#'" class="text-nav font-medium hover:underline">
                    {{ item.title }}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        <div class="mt-4 text-sm text-description text-gray-400">
          <span>Photos de Marie Lachaud</span>
        </div>
      </Container>
    </footer>
  </div>
</template>
